import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Building, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import api from "../lib/api";

const emptyForm = {
  legal_name: "",
  legal_id: "",
  trade_name: "",
  address: "",
  phone: "",
  email: "",
  is_primary: false,
};

export default function Companies({ embedded = false }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
    } catch {
      toast.error("Error al cargar empresas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      legal_name: c.legal_name || "",
      legal_id: c.legal_id || "",
      trade_name: c.trade_name || "",
      address: c.address || "",
      phone: c.phone || "",
      email: c.email || "",
      is_primary: !!c.is_primary,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.legal_name || !form.legal_id) {
      toast.error("Razón social y cédula jurídica son requeridas");
      return;
    }
    try {
      if (editId) {
        await api.put(`/companies/${editId}`, form);
        toast.success("Empresa actualizada");
      } else {
        await api.post("/companies", form);
        toast.success("Empresa creada");
      }
      setDialogOpen(false);
      fetchCompanies();
    } catch {
      toast.error("Error al guardar empresa");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta empresa?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Empresa eliminada");
      fetchCompanies();
    } catch {
      toast.error("Error al eliminar empresa");
    }
  };

  return (
    <div data-testid="companies-page">
      <div className="flex items-center justify-between mb-6">
        {!embedded && (
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
              Empresas
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Gestión de las razones sociales del grupo
            </p>
          </div>
        )}
        {embedded && <div />}
        <Button
          onClick={openCreate}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="add-company-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva empresa
        </Button>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Razón social
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Cédula jurídica
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">
                  Nombre comercial
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden lg:table-cell">
                  Teléfono
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Principal
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((c) => (
                <TableRow
                  key={c.id}
                  className="border-zinc-100"
                  data-testid={`company-row-${c.id}`}
                >
                  <TableCell className="text-sm text-zinc-900 font-medium">
                    {c.legal_name}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 font-mono">
                    {c.legal_id}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden md:table-cell">
                    {c.trade_name || "\u2014"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden lg:table-cell">
                    {c.phone || "\u2014"}
                  </TableCell>
                  <TableCell>
                    {c.is_primary ? (
                      <Badge
                        variant="outline"
                        className="bg-amber-50 text-amber-700 border-amber-200 font-medium"
                      >
                        <Star className="w-3 h-3 mr-1 fill-amber-500 stroke-amber-500" />
                        Principal
                      </Badge>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(c)}
                      data-testid={`edit-company-${c.id}`}
                    >
                      <Pencil className="w-4 h-4 text-zinc-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(c.id)}
                      data-testid={`delete-company-${c.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {companies.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-zinc-400">
                    <Building className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay empresas registradas</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editId ? "Editar empresa" : "Nueva empresa"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Razón social *
              </Label>
              <Input
                value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                placeholder="Ciro del Pacífico S.A."
                data-testid="company-legal-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Cédula jurídica *
                </Label>
                <Input
                  value={form.legal_id}
                  onChange={(e) => setForm({ ...form, legal_id: e.target.value })}
                  placeholder="3-101-XXXXXX"
                  data-testid="company-legal-id-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Nombre comercial
                </Label>
                <Input
                  value={form.trade_name}
                  onChange={(e) => setForm({ ...form, trade_name: e.target.value })}
                  placeholder="VETCOMER"
                  data-testid="company-trade-name-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">Dirección</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="San José, Costa Rica"
                data-testid="company-address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">Teléfono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+506 2222-3333"
                  data-testid="company-phone-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">Correo</Label>
                <Input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="info@empresa.com"
                  data-testid="company-email-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                checked={form.is_primary}
                onCheckedChange={(v) => setForm({ ...form, is_primary: !!v })}
                id="is_primary"
                data-testid="company-is-primary-checkbox"
              />
              <Label htmlFor="is_primary" className="text-sm text-zinc-700 cursor-pointer">
                Marcar como empresa principal
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="company-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="company-submit-button"
            >
              {editId ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
